/**
 * HomeQuickFilters — barre de pills glass sous le HomeHero pour filtrer
 * les biens sans recharger la search bar (filtres indépendants).
 *
 * Comportement :
 *  - 4 pills (Type / Prix min / Prix max / Chambres) ouvrent chacun un Popover
 *  - 1 bouton "Plus de filtres" ouvre un Sheet latéral (drawer droit)
 *  - Compteur live "X biens trouvés" debouncé (400 ms) via /api/properties?limit=1
 *  - Bouton "Voir les résultats" (et clic compteur) navigue vers /properties?...
 *  - Bouton "Effacer" si ≥ 1 filtre actif — pill prend un badge orange si actif
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, Home as HomeIcon, Building2, Trees, Briefcase, Bed,
  SlidersHorizontal, X, Loader2, Search, MapPin, Bath, Car, ShieldCheck,
  Wifi, Sparkles, Store, Hammer, Landmark, PartyPopper,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetFooter,
} from './ui/sheet';
import { propertyAPI } from '../utils/api';
import { useTaxonomy } from '../hooks/useTaxonomy';
import FilterFab from './FilterFab';

// --- Icon mapping per type value ---
// The list of values comes from `useTaxonomy()` (live fetch with fallback).
const TYPE_ICON = {
  house: HomeIcon,
  apartment: Building2,
  villa: HomeIcon,
  duplex: Building2,
  studio: Bed,
  office: Briefcase,
  shop: Store,
  commercial: Briefcase,
  land: Trees,
  development: Hammer,
  bank_repossession: Landmark,
  event_hall: PartyPopper,
};

const PRICE_STEPS = [
  0, 100_000, 250_000, 500_000, 1_000_000, 2_500_000,
  5_000_000, 10_000_000, 25_000_000, 50_000_000, 100_000_000,
];

const BEDROOM_OPTIONS = [
  { value: null, label: 'Indifférent', isRange: false },
  { value: 0, label: 'Studio', isRange: false },
  { value: 1, label: '1', isRange: false },
  { value: 2, label: '2', isRange: false },
  { value: 3, label: '3', isRange: false },
  { value: 4, label: '4+', isRange: true },
];

// Map a bedrooms value to its bedroomsMax counterpart.
// Returns null for the "4+" option (open-ended upper bound) and null/"all".
const bedroomsMaxFor = (value) => {
  if (value == null) return null;
  const opt = BEDROOM_OPTIONS.find((o) => o.value === value);
  return opt && !opt.isRange ? value : null;
};

const AMENITIES = [
  { value: 'parking',   label: 'Parking',     Icon: Car },
  { value: 'security',  label: 'Sécurité 24h', Icon: ShieldCheck },
  { value: 'wifi',      label: 'WiFi',        Icon: Wifi },
];

const fmt = (n) => `${(Math.round(n) || 0).toLocaleString('fr-FR').replaceAll(',', ' ')} FCFA`;

// --- Pill component ---
const FilterPill = ({ active, label, value, onClear, children, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        data-testid={testid}
        className={`group relative h-12 px-4 sm:px-5 rounded-xl border transition-all flex items-center gap-2 min-w-[120px] text-sm font-medium
          ${active
            ? 'bg-white border-brand-400 text-stone-900 shadow-md'
            : 'bg-white/85 backdrop-blur-xl border-white/40 text-stone-700 hover:bg-white hover:border-brand-300'}`}
      >
        <span className="flex-1 text-left truncate">
          <span className={`block text-label uppercase tracking-wider font-semibold ${active ? 'text-brand-600' : 'text-stone-500'}`}>
            {label}
          </span>
          <span className="block text-sm leading-tight truncate">{value}</span>
        </span>
        {active ? (
          <X
            className="h-3.5 w-3.5 text-stone-500 hover:text-rose-500 shrink-0"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClear?.(); }}
          />
        ) : (
          <ChevronDown className="h-4 w-4 text-stone-500 group-hover:text-stone-700 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        )}
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" sideOffset={8} className="w-72 p-2">
      {children}
    </PopoverContent>
  </Popover>
);

// --- Price list (used for both Min and Max) ---
const PriceList = ({ value, onSelect, isMax = false, minBound = 0 }) => {
  const options = PRICE_STEPS.filter((p) => (isMax ? p > minBound : true));
  return (
    <div className="max-h-64 overflow-y-auto">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`w-full px-3 py-2 text-left text-sm rounded-md hover:bg-stone-100 ${value == null ? 'bg-brand-50 text-brand-700 font-medium' : ''}`}
      >
        Indifférent
      </button>
      {options.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onSelect(p)}
          className={`w-full px-3 py-2 text-left text-sm rounded-md hover:bg-stone-100 ${value === p ? 'bg-brand-50 text-brand-700 font-medium' : ''}`}
          data-testid={`price-opt-${isMax ? 'max' : 'min'}-${p}`}
        >
          {p === 0 ? 'À partir de 0' : fmt(p)}
        </button>
      ))}
    </div>
  );
};

const HomeQuickFilters = ({
  variant = 'hero',
  initialValues = null,
  onChange = null,
  showSearchInput = false,
}) => {
  const navigate = useNavigate();
  const isCompact = variant === 'compact';
  const [type, setType] = useState(initialValues?.type ?? null);
  const [minPrice, setMinPrice] = useState(initialValues?.minPrice ?? null);
  const [maxPrice, setMaxPrice] = useState(initialValues?.maxPrice ?? null);
  const [bedrooms, setBedrooms] = useState(initialValues?.bedrooms ?? null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Audit fix 8.2 — unique id per mount to prevent duplicate `search-input` DOM ids
  // when this component renders on both Home + SRP under the same tree.
  const searchInputId = useId();
  // Mobile-only collapsed state for the pills row. On `sm:` and up the
  // CSS forces them visible. Pattern aligned with the footer accordion
  // and Booking/Airbnb mobile-filter UX.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Extra filters (drawer)
  const [city, setCity] = useState(initialValues?.city ?? '');
  const [bathrooms, setBathrooms] = useState(initialValues?.bathrooms ?? null);
  const [minSurface, setMinSurface] = useState(initialValues?.minSurface ?? '');
  const [amenities, setAmenities] = useState(initialValues?.amenities ?? []); // array of values
  const [search, setSearch] = useState(initialValues?.search ?? '');
  // "Meublé" toggle chip — `null` = indifferent, `true` = meublé seulement.
  const [furnished, setFurnished] = useState(
    initialValues?.furnished === true ? true : null
  );

  // Live count
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  // Facets (counts per filter option) — fetched in parallel with count
  const [facets, setFacets] = useState({ type: {}, bedrooms: {}, city: [], neighborhood: [] });
  const [maxSurface, setMaxSurface] = useState(initialValues?.maxSurface ?? '');
  const [neighborhood, setNeighborhood] = useState(initialValues?.neighborhood ?? '');

  // Sync external → internal state when `initialValues` changes (e.g. the
  // parent Properties.jsx cleared filters via the ActiveFiltersBar reset
  // or a Link changed the URL query params). Without this, the sticky
  // compact bar keeps displaying stale chip values while the underlying
  // list/URL are already up-to-date, giving the user contradictory signals.
  //
  // Symmetric with the searchParams → filters effect on Properties.jsx.
  // We depend on a serialised signature to avoid reference-based reruns
  // when the parent creates a new `initialValues` object every render.
  const initialSignature = JSON.stringify(initialValues || {});
  useEffect(() => {
    if (!initialValues) return;
    setType(initialValues.type ?? null);
    setMinPrice(initialValues.minPrice ?? null);
    setMaxPrice(initialValues.maxPrice ?? null);
    setBedrooms(initialValues.bedrooms ?? null);
    setBathrooms(initialValues.bathrooms ?? null);
    setCity(initialValues.city ?? '');
    setNeighborhood(initialValues.neighborhood ?? '');
    setMinSurface(initialValues.minSurface ?? '');
    setMaxSurface(initialValues.maxSurface ?? '');
    setAmenities(initialValues.amenities ?? []);
    setSearch(initialValues.search ?? '');
    setFurnished(initialValues.furnished === true ? true : null);
  }, [initialSignature]);

  const params = useMemo(() => {
    const p = {};
    if (type) p.type = type;
    if (minPrice != null) p.minPrice = minPrice;
    if (maxPrice != null) p.maxPrice = maxPrice;
    if (bedrooms != null) p.bedrooms = bedrooms;
    const bedMax = bedroomsMaxFor(bedrooms);
    if (bedMax != null) p.bedroomsMax = bedMax;
    if (bathrooms != null) p.bathrooms = bathrooms;
    if (city.trim()) p.city = city.trim();
    if (neighborhood.trim()) p.neighborhood = neighborhood.trim();
    if (minSurface) p.minSurface = minSurface;
    if (maxSurface) p.maxSurface = maxSurface;
    if (search.trim()) p.search = search.trim();
    if (furnished === true) p.furnished = true;
    return p;
  }, [type, minPrice, maxPrice, bedrooms, bathrooms, city, neighborhood, minSurface, maxSurface, search, furnished]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (type) n++;
    if (minPrice != null) n++;
    if (maxPrice != null) n++;
    if (bedrooms != null) n++;
    if (bathrooms != null) n++;
    if (city.trim()) n++;
    if (neighborhood.trim()) n++;
    if (furnished === true) n++;
    if (minSurface) n++;
    if (maxSurface) n++;
    if (amenities.length) n += amenities.length;
    return n;
  }, [type, minPrice, maxPrice, bedrooms, bathrooms, city, neighborhood, amenities, furnished, minSurface, maxSurface]);

  // Debounce the free-text search input so we don't issue an API call on
  // every keystroke. Other filters propagate immediately.
  const [debouncedSearch, setDebouncedSearch] = useState(initialValues?.search ?? '');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // C4 v4 — Autocomplete suggestions for the search input. We hit the
  // light `/api/properties/suggest` endpoint which returns a mix of
  // synonym dictionary terms and matching city/neighborhood names from
  // approved listings — guiding visitors toward queries that have results.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestItems, setSuggestItems] = useState([]);
  useEffect(() => {
    const q = (search || '').trim();
    if (q.length < 2) {
      setSuggestItems([]);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/properties/suggest?q=${encodeURIComponent(q)}&limit=8`,
          { signal: ctrl.signal },
        );
        if (!r.ok) return;
        const data = await r.json();
        if (!ctrl.signal.aborted) setSuggestItems(data.suggestions || []);
      } catch {
        /* silent */
      }
    }, 180);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [search]);

  // Propagate filter changes upward in compact mode so the parent
  // listing page (e.g. `/properties`) can re-query the API live.
  // `onChange` is a stable callback in our usage; keeping it out of deps
  // avoids resetting parent state in a feedback loop.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => {
    if (!onChangeRef.current) return;
    onChangeRef.current({
      type, minPrice, maxPrice, bedrooms,
      bedroomsMax: bedroomsMaxFor(bedrooms),
      bathrooms,
      city: city.trim(), search: debouncedSearch.trim(), amenities,
      furnished, minSurface, maxSurface,
      neighborhood: neighborhood.trim(),
    });
  }, [type, minPrice, maxPrice, bedrooms, bathrooms, city, debouncedSearch, amenities, furnished, minSurface, maxSurface, neighborhood]);

  // Debounced live count + facets (parallel)
  const tRef = useRef(null);
  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      setLoadingCount(true);
      try {
        const [listRes, facetsRes] = await Promise.all([
          propertyAPI.list({ ...params, limit: 1 }),
          propertyAPI.getFacets(params).catch(() => ({ data: { type: {}, bedrooms: {}, city: [] } })),
        ]);
        const total = parseInt(listRes.headers?.['x-total-count'] ?? listRes.headers?.['X-Total-Count'] ?? '0', 10);
        setCount(Number.isFinite(total) ? total : (Array.isArray(listRes.data) ? listRes.data.length : 0));
        const f = facetsRes.data || {};
        setFacets({
          type: f.type || {},
          bedrooms: f.bedrooms || {},
          city: Array.isArray(f.city) ? f.city : [],
          neighborhood: Array.isArray(f.neighborhood) ? f.neighborhood : [],
        });
      } catch {
        setCount(null);
      } finally {
        setLoadingCount(false);
      }
    }, 400);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [params]);

  const clearAll = useCallback(() => {
    setType(null); setMinPrice(null); setMaxPrice(null); setBedrooms(null);
    setBathrooms(null); setCity(''); setNeighborhood(''); setMinSurface(''); setMaxSurface('');
    setAmenities([]); setFurnished(null);
  }, []);

  const goToResults = useCallback(() => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => sp.set(k, String(v)));
    amenities.forEach((a) => sp.append('amenity', a));
    navigate(`/properties?${sp.toString()}`);
  }, [params, amenities, navigate]);

  const toggleAmenity = (v) =>
    setAmenities((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));

  // Live taxonomy (fetched from /api/taxonomy/properties with fallback)
  const { types: taxonomyTypes } = useTaxonomy();
  const PROPERTY_TYPES = useMemo(
    () => taxonomyTypes.map((t) => ({ ...t, Icon: TYPE_ICON[t.value] || HomeIcon })),
    [taxonomyTypes],
  );

  // Defensive: if the URL-hydrated `type` value is NOT in the live taxonomy,
  // drop it. This prevents a desynced UI where the pill is active with an
  // unknown label while the URL keeps an unrecognized constraint.
  useEffect(() => {
    if (!type || PROPERTY_TYPES.length === 0) return;
    const known = PROPERTY_TYPES.some((t) => t.value === type);
    if (!known) setType(null);
  }, [type, PROPERTY_TYPES]);

  // Type label — fall back to a sanitized value if a non-taxonomy type slips through.
  const typeLabel = type
    ? (PROPERTY_TYPES.find((t) => t.value === type)?.label || 'Tous')
    : 'Tous';
  const bedLabel = bedrooms == null
    ? 'Indifférent'
    : (BEDROOM_OPTIONS.find((o) => o.value === bedrooms)?.label ?? `${bedrooms}+`);

  return (
    <section
      id="filters-section"
      className={isCompact
        ? 'sticky top-[65px] z-30 bg-white/95 backdrop-blur-md border-b border-stone-200 shadow-sm'
        : 'relative mt-4 sm:mt-5 z-20'}
      data-testid="home-quick-filters"
    >
      <div className={isCompact ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
        <div className={isCompact
          ? 'py-3'
          : 'bg-white/85 backdrop-blur-2xl border border-white/40 shadow-xl rounded-2xl p-3 sm:p-4'}
        >
          {/* Optional inline search (compact only) */}
          {isCompact && showSearchInput && (
            <div className="relative mb-2.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
              <Input
                id={searchInputId}
                type="search"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSuggestOpen(true); }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Flush debounce immediately so the parent re-queries now.
                    setDebouncedSearch(search.trim());
                    setSuggestOpen(false);
                  } else if (e.key === 'Escape') {
                    setSuggestOpen(false);
                  }
                }}
                placeholder="Rechercher par ville, quartier, mots-clés…"
                className="h-11 pl-10 rounded-xl"
                data-testid="filter-compact-search"
                aria-label="Rechercher un bien"
              />
              {/* C4 v4 — Suggestions dropdown. Click selects the term and
                  triggers an immediate re-query via the same debounce flush. */}
              {suggestOpen && suggestItems.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-stone-200 shadow-lg z-30 overflow-hidden"
                  data-testid="search-suggest-dropdown"
                >
                  <ul className="max-h-64 overflow-y-auto py-1">
                    {suggestItems.map((s) => (
                      <li key={`${s.type}-${s.label}`}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSearch(s.label);
                            setDebouncedSearch(s.label);
                            setSuggestOpen(false);
                          }}
                          className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-stone-50 transition-colors"
                          data-testid={`search-suggest-${s.type}-${s.label.replace(/\W+/g, '-').toLowerCase()}`}
                        >
                          <span className="flex items-center gap-2">
                            {s.type === 'synonym' && <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">Type</span>}
                            {s.type === 'city' && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">Ville</span>}
                            {s.type === 'neighborhood' && <span className="text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">Quartier</span>}
                            <span className="text-stone-800 capitalize">{s.label}</span>
                          </span>
                          {typeof s.count === 'number' && (
                            <span className="text-xs text-stone-500">{s.count} annonce{s.count > 1 ? 's' : ''}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Pills row */}
          <div
            id="quick-filters-pills"
            className={`${isCompact && !mobileFiltersOpen ? 'hidden' : 'flex'} sm:flex flex-wrap items-center gap-2 sm:gap-3`}
          >
            {/* Type */}
            <FilterPill
              testid="filter-pill-type"
              label="Type"
              value={typeLabel}
              active={!!type}
              onClear={() => setType(null)}
            >
              <div className="max-h-72 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setType(null)}
                  className={`w-full px-3 py-2 rounded-md text-sm hover:bg-stone-100 text-left ${type == null ? 'bg-brand-50 text-brand-700 font-medium' : ''}`}
                >
                  Tous types
                </button>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  {PROPERTY_TYPES.map(({ value, label, Icon }) => {
                    const fc = facets.type?.[value];
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setType(value)}
                        data-testid={`filter-type-${value}`}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-xs hover:bg-stone-100 text-left ${type === value ? 'bg-brand-50 text-brand-700 font-medium' : ''} ${fc === 0 ? 'opacity-50' : ''}`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate flex-1">{label}</span>
                        {fc != null && (
                          <span className="text-label text-stone-500 tabular-nums">({fc})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </FilterPill>

            {/* Min Price */}
            <FilterPill
              testid="filter-pill-min"
              label="Prix min"
              value={minPrice == null ? 'Min' : fmt(minPrice)}
              active={minPrice != null}
              onClear={() => setMinPrice(null)}
            >
              <PriceList value={minPrice} onSelect={setMinPrice} />
            </FilterPill>

            {/* Max Price */}
            <FilterPill
              testid="filter-pill-max"
              label="Prix max"
              value={maxPrice == null ? 'Max' : fmt(maxPrice)}
              active={maxPrice != null}
              onClear={() => setMaxPrice(null)}
            >
              <PriceList value={maxPrice} onSelect={setMaxPrice} isMax minBound={minPrice ?? 0} />
            </FilterPill>

            {/* Bedrooms */}
            <FilterPill
              testid="filter-pill-bedrooms"
              label="Chambres"
              value={bedLabel}
              active={bedrooms != null}
              onClear={() => setBedrooms(null)}
            >
              <div className="grid grid-cols-3 gap-1.5">
                {BEDROOM_OPTIONS.map(({ value, label }) => {
                  // For studio key="0", for "4+" key="4+"
                  const facetKey = value === 0 ? '0' : value === 4 ? '4+' : value != null ? String(value) : null;
                  const fc = facetKey ? facets.bedrooms?.[facetKey] : null;
                  return (
                    <button
                      key={String(value)}
                      type="button"
                      onClick={() => setBedrooms(value)}
                      data-testid={`filter-bedrooms-${value ?? 'any'}`}
                      className={`px-3 py-2 rounded-md text-sm hover:bg-stone-100 inline-flex items-center justify-center gap-1 ${bedrooms === value ? 'bg-brand-50 text-brand-700 font-medium' : ''} ${fc === 0 ? 'opacity-50' : ''}`}
                    >
                      {value !== null && value !== undefined && <Bed className="h-3.5 w-3.5" />}
                      <span>{label}</span>
                      {fc != null && (
                        <span className="text-label text-stone-500 tabular-nums">({fc})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </FilterPill>

            {/* Furnished — toggle chip (no popover) */}
            <button
              type="button"
              onClick={() => setFurnished((f) => (f === true ? null : true))}
              data-testid="filter-chip-furnished"
              aria-pressed={furnished === true}
              className={`group h-12 px-4 sm:px-5 rounded-xl border transition-all flex items-center gap-2 text-sm font-medium ${
                furnished === true
                  ? 'bg-brand-600 border-brand-600 text-white shadow-md hover:bg-brand-700'
                  : 'bg-white/85 backdrop-blur-xl border-white/40 text-stone-700 hover:bg-white hover:border-brand-300'
              }`}
            >
              <Sparkles
                className={`h-4 w-4 ${
                  furnished === true ? 'text-white' : 'text-brand-500'
                }`}
              />
              <span>Meublé</span>
              {furnished === true && (
                <X
                  className="h-3.5 w-3.5 text-white/80 hover:text-white shrink-0"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFurnished(null);
                  }}
                />
              )}
            </button>

            {/* More filters → Sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="filter-pill-more"
                  className="h-12 px-4 sm:px-5 rounded-xl border border-stone-900 bg-stone-900 text-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-stone-800 transition-colors relative"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Plus de filtres
                  {amenities.length + (bathrooms != null ? 1 : 0) + (city.trim() ? 1 : 0) > 0 && (
                    <Badge className="bg-brand-500 text-white border-0 text-label h-5 min-w-5 px-1.5">
                      {amenities.length + (bathrooms != null ? 1 : 0) + (city.trim() ? 1 : 0)}
                    </Badge>
                  )}
                </button>
              </SheetTrigger>
              {/* Audit fix 5.1 — bottom sheet on mobile so the filter panel
                  never overflows off-screen. Desktop keeps a comfortable
                  centered look via max-w + rounded top corners. */}
              <SheetContent
                side="bottom"
                className="max-h-[85dvh] overflow-y-auto rounded-t-2xl sm:max-w-2xl sm:mx-auto sm:inset-x-0"
                data-testid="filter-sheet"
              >
                <SheetHeader>
                  <SheetTitle className="font-display text-2xl">Affiner la recherche</SheetTitle>
                  <SheetDescription>
                    Précisez vos critères — le compteur se met à jour automatiquement.
                  </SheetDescription>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  {/* City */}
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-brand-500" /> Ville / Quartier
                    </Label>
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Douala, Bonanjo, Yaoundé…"
                      className="mt-2"
                      data-testid="filter-sheet-city"
                    />
                    {/* Top-cities suggestions with live counts */}
                    {facets.city.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {facets.city.slice(0, 8).map((c) => {
                          const active = city.trim().toLowerCase() === c.value.toLowerCase();
                          return (
                            <button
                              key={c.value}
                              type="button"
                              onClick={() => { setCity(active ? '' : c.value); setNeighborhood(''); }}
                              data-testid={`filter-city-suggest-${c.value}`}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-brand-50 border-brand-400 text-brand-700 font-semibold' : 'border-stone-200 hover:bg-stone-50 text-stone-700'} ${c.count === 0 ? 'opacity-50' : ''}`}
                            >
                              {c.value} <span className="text-label text-stone-500">({c.count})</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Quartier — quartier-level filter scoped to active city */}
                  {facets.neighborhood.length > 0 && (
                    <div data-testid="filter-neighborhood-section">
                      <Label className="text-sm font-semibold flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-brand-500" /> Quartier
                        {city && <span className="text-label font-normal text-stone-500">· {city}</span>}
                      </Label>
                      <div className="mt-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                        {facets.neighborhood.map((n) => {
                          const active = neighborhood.trim().toLowerCase() === n.value.toLowerCase();
                          return (
                            <button
                              key={n.value}
                              type="button"
                              onClick={() => setNeighborhood(active ? '' : n.value)}
                              data-testid={`filter-neighborhood-${n.value}`}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-brand-50 border-brand-400 text-brand-700 font-semibold' : 'border-stone-200 hover:bg-stone-50 text-stone-700'} ${n.count === 0 ? 'opacity-50' : ''}`}
                            >
                              {n.value} <span className="text-label text-stone-500">({n.count})</span>
                            </button>
                          );
                        })}
                      </div>
                      {!city && (
                        <p className="text-label text-stone-500 mt-1.5">💡 Choisissez d'abord une ville pour filtrer plus précisément.</p>
                      )}
                    </div>
                  )}

                  {/* Bathrooms */}
                  <div>
                    <Label className="text-sm font-semibold flex items-center gap-1.5">
                      <Bath className="h-4 w-4 text-brand-500" /> Salles de bain
                    </Label>
                    <div className="grid grid-cols-5 gap-1.5 mt-2">
                      {[null, 1, 2, 3, 4].map((b) => (
                        <button
                          key={String(b)}
                          type="button"
                          onClick={() => setBathrooms(b)}
                          data-testid={`filter-bath-${b ?? 'any'}`}
                          className={`px-3 py-2 rounded-md border text-sm ${bathrooms === b ? 'bg-brand-50 border-brand-400 text-brand-700 font-medium' : 'border-stone-200 hover:bg-paper'}`}
                        >
                          {b == null ? 'Tout' : (b === 4 ? '4+' : b)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Surface range (min / max) */}
                  <div>
                    <Label className="text-sm font-semibold">Surface (m²)</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Input
                        type="number"
                        min="0"
                        value={minSurface}
                        onChange={(e) => setMinSurface(e.target.value)}
                        placeholder="Min (ex: 80)"
                        data-testid="filter-sheet-surface-min"
                      />
                      <Input
                        type="number"
                        min="0"
                        value={maxSurface}
                        onChange={(e) => setMaxSurface(e.target.value)}
                        placeholder="Max (ex: 300)"
                        data-testid="filter-sheet-surface-max"
                      />
                    </div>
                  </div>

                  {/* Amenities */}
                  <div>
                    <Label className="text-sm font-semibold">Équipements & services</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {AMENITIES.map(({ value, label, Icon }) => {
                        const on = amenities.includes(value);
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => toggleAmenity(value)}
                            data-testid={`filter-amenity-${value}`}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${on ? 'bg-brand-50 border-brand-400 text-brand-700 font-medium' : 'border-stone-200 hover:bg-paper'}`}
                          >
                            <Icon className="h-4 w-4" /> {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <SheetFooter className="gap-2 sm:flex-row flex-col">
                  <Button variant="outline" onClick={clearAll} className="sm:w-auto" data-testid="filter-sheet-clear">
                    <X className="h-4 w-4 mr-1" /> Effacer tout
                  </Button>
                  <Button
                    onClick={() => { setSheetOpen(false); goToResults(); }}
                    className="bg-brand-600 hover:bg-brand-700 text-white flex-1"
                    data-testid="filter-sheet-apply"
                  >
                    {loadingCount ? (
                      <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</span>
                    ) : (
                      <>Voir {count ?? 0} résultat{(count || 0) > 1 ? 's' : ''}</>
                    )}
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          {/* Live counter + actions — hidden in compact mode (parent handles its own count/CTA) */}
          {!isCompact && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 pt-3 border-t border-stone-200/70">
            <div className="text-sm text-stone-600 flex items-center gap-2" data-testid="filter-counter">
              {loadingCount ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin text-brand-500" /> Mise à jour…</>
              ) : count == null ? (
                <>Définissez vos critères pour découvrir les biens disponibles.</>
              ) : (
                <>
                  <span className="font-display font-bold text-stone-900 text-base">{count.toLocaleString('fr-FR').replaceAll(',', ' ')}</span>
                  {count > 1 ? 'biens correspondent à vos critères' : 'bien correspond à vos critères'}
                  {activeCount > 0 && (
                    <Badge variant="outline" className="ml-1 border-brand-300 text-brand-700 text-label">
                      {activeCount} filtre{activeCount > 1 ? 's' : ''} actif{activeCount > 1 ? 's' : ''}
                    </Badge>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  data-testid="filter-clear-all"
                  className="text-stone-600 hover:text-rose-600"
                >
                  <X className="h-4 w-4 mr-1" /> Effacer
                </Button>
              )}
              <Button
                onClick={goToResults}
                disabled={!count}
                className="bg-brand-600 hover:bg-brand-700 text-white font-semibold"
                data-testid="filter-see-results"
              >
                <Search className="h-4 w-4 mr-1.5" />
                Voir les résultats
              </Button>
            </div>
          </div>
          )}

          {/* Compact mode: just a small clear button on the right of the pills */}
          {isCompact && activeCount > 0 && (
            <div className="flex items-center justify-end mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                data-testid="filter-clear-all-compact"
                className="text-stone-600 hover:text-rose-600 text-xs"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Effacer {activeCount} filtre{activeCount > 1 ? 's' : ''}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Floating sticky-bottom "Filtres" pill — mobile only, Booking/Airbnb iOS pattern.
          Reusable across /properties, /services, /legal-pros via the FilterFab component. */}
      {isCompact && (
        <FilterFab
          open={mobileFiltersOpen}
          onToggle={() => setMobileFiltersOpen((o) => !o)}
          activeCount={activeCount}
          ariaControls="quick-filters-pills"
        />
      )}
    </section>
  );
};

export default HomeQuickFilters;
